import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cloud } from 'lucide-react';
import { toast } from 'sonner';

import { PROXY_BASE } from '@/lib/proxy';
import { getPref, setPref, PREF_KEYS } from '@/db/prefs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Toggle } from '@/components/ui/toggle';
import { listCredentialRows } from '@/db/credentials';
import type { CredentialRow } from '@/db/schema';
import { getSource } from '@/sources/registry';
import { BlacklistEditor } from '@/features/blacklist';
import { copyDebugReport, getManualDebugReportText } from '@/features/diagnostics';
import { DebugReportFallbackDialog } from '@/features/diagnostics/DebugReportFallbackDialog';

export function SettingsPage() {
  const [proxy, setProxy] = useState<string>(PROXY_BASE);
  const [dark, setDark] = useState<boolean>(() =>
    document.documentElement.classList.contains('dark'),
  );
  const [creds, setCreds] = useState<CredentialRow[]>([]);
  const [manualDebugReportText, setManualDebugReportText] = useState<string | null>(null);

  useEffect(() => {
    void getPref<string>(PREF_KEYS.PROXY_BASE).then((p) => {
      if (p) setProxy(p);
    });
    void listCredentialRows().then(setCreds);
  }, []);

  async function saveProxy() {
    await setPref(PREF_KEYS.PROXY_BASE, proxy);
    toast.success('Proxy URL saved. Reload to pick up the change.');
  }

  function toggleTheme(value: boolean) {
    setDark(value);
    document.documentElement.classList.toggle('dark', value);
    void setPref(PREF_KEYS.THEME, value ? 'dark' : 'light');
  }

  async function copyDiagnostics() {
    try {
      await copyDebugReport();
      toast.success('Debug report copied.');
    } catch (err) {
      const reportText = getManualDebugReportText(err);
      if (reportText) {
        setManualDebugReportText(reportText);
        return;
      }
      const message = err instanceof Error ? err.message : String(err);
      toast.error(`Could not copy debug report: ${message}`);
    }
  }

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <h1 className="text-2xl font-bold">Settings</h1>

      <Card>
        <CardHeader>
          <CardTitle>Account sync</CardTitle>
          <CardDescription>
            Bunko accounts sync settings, library state, and source credentials
            across signed-in devices. This is account-authenticated cloud sync,
            not device-approved end-to-end sync.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/account">
            <Button variant="outline">
              <Cloud className="mr-1.5 h-4 w-4" />
              Open Bunko account
            </Button>
          </Link>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Connected accounts</CardTitle>
          <CardDescription>
            Per-source cookie stores. Use the Connect Account screens to add or
            refresh.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {creds.length === 0 ? (
            <p className="text-sm text-muted-foreground">No accounts connected.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {creds.map((c) => (
                <li key={c.source} className="flex items-center justify-between">
                  <span>
                    <strong>{getSource(c.source)?.name ?? c.source}</strong>
                    {c.mode === 'anonymous'
                      ? ' · anonymous (CF cookies only)'
                      : c.username
                        ? ` · ${c.username}`
                        : ''}
                  </span>
                  <span
                    className={
                      c.status === 'ok'
                        ? 'text-xs text-emerald-500'
                        : 'text-xs text-amber-500'
                    }
                  >
                    {c.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Proxy URL</CardTitle>
          <CardDescription>
            Base URL of the Cloudflare Worker proxy. Set <code>VITE_PROXY_BASE</code>{' '}
            at build time, or override per-device here.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Input value={proxy} onChange={(e) => setProxy(e.target.value)} />
          <Button onClick={saveProxy}>Save</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Diagnostics</CardTitle>
          <CardDescription>
            Copy a safe debug report with recent reader/source breadcrumbs and
            local cache summaries. Cookies and source tokens are redacted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={copyDiagnostics}>
            Copy debug report
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Theme</CardTitle>
          <CardDescription>Dark by default; class-based toggle.</CardDescription>
        </CardHeader>
        <CardContent>
          <Toggle pressed={dark} onPressedChange={toggleTheme} variant="outline">
            {dark ? 'Dark' : 'Light'}
          </Toggle>
        </CardContent>
      </Card>

      <BlacklistEditor />
      <DebugReportFallbackDialog
        reportText={manualDebugReportText}
        onOpenChange={(open) => {
          if (!open) setManualDebugReportText(null);
        }}
      />
    </div>
  );
}
